import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, DollarSign, AlertTriangle, LayoutTemplate, RotateCcw, Save, Building2 } from "lucide-react";
import {
  getPricingSetting, updatePricingSetting,
  getLandingSetting, updateLandingSetting,
  getCompanySetting, updateCompanySetting,
} from "@/lib/api";

const inputCls =
  "bg-[#0E1424] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-[#1E6BFF]/50";

// Editable public Landing hero copy. Fields map 1:1 to the backend hero model.
const HERO_FIELDS = [
  { key: "badge", label: "Eyebrow / badge", type: "input", placeholder: "TeamBuilder AI\u2122 \u2014 Workforce Blueprint Generator" },
  { key: "title", label: "Headline", type: "input", placeholder: "Design your optimized global talent team in minutes." },
  { key: "highlight", label: "Highlighted phrase (optional)", type: "input", placeholder: "global talent team", hint: "This exact phrase inside the headline is shown in the brand blue. Leave blank for no emphasis." },
  { key: "subtitle", label: "Subtitle", type: "textarea", placeholder: "Answer a short, consultative assessment..." },
  { key: "cta", label: "Primary button label", type: "input", placeholder: "Start Your Assessment" },
];

const LandingHeroEditor = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hero, setHero] = useState(null);
  const [defaults, setDefaults] = useState(null);

  useEffect(() => {
    let active = true;
    getLandingSetting(token)
      .then((d) => {
        if (!active) return;
        setHero(d.hero || {});
        setDefaults(d.defaults || {});
      })
      .catch(() => toast.error("Failed to load landing content"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const setField = (key, value) => setHero((h) => ({ ...h, [key]: value }));

  const save = async (payload) => {
    setSaving(true);
    try {
      const res = await updateLandingSetting(token, payload);
      setHero(res.hero || {});
      toast.success("Landing hero updated \u2014 live on the public page now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save landing content");
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => save(hero || {});
  const onReset = () => {
    if (!defaults) return;
    setHero({ ...defaults });
    save({ ...defaults });
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-5 max-w-2xl flex items-center gap-2 text-white/50">
        <Loader2 size={16} className="animate-spin" /> Loading landing content...
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-5 max-w-2xl" data-testid="settings-landing-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-[#66B2FF]/10 p-2 border border-[#66B2FF]/20">
          <LayoutTemplate size={18} className="text-[#66B2FF]" />
        </div>
        <div className="flex-1">
          <span className="text-white font-medium">Landing Hero Text</span>
          <p className="text-white/55 text-sm mt-1 max-w-md">
            Edit the headline, subtitle, and button label shown at the top of the public Landing page.
            Changes go live immediately.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {HERO_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-white/70 text-xs font-medium">{f.label}</label>
            {f.type === "textarea" ? (
              <Textarea
                value={hero?.[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={3}
                data-testid={`settings-landing-${f.key}`}
                className={`${inputCls} mt-1 resize-y min-h-[80px]`}
              />
            ) : (
              <Input
                value={hero?.[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`settings-landing-${f.key}`}
                className={`${inputCls} mt-1 h-10`}
              />
            )}
            {f.hint && <p className="text-white/35 text-xs mt-1">{f.hint}</p>}
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90"
          data-testid="settings-landing-save"
        >
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />}
          Save Changes
        </Button>
        <Button
          variant="ghost"
          onClick={onReset}
          disabled={saving}
          className="text-white/70 hover:text-white hover:bg-white/5"
          data-testid="settings-landing-reset"
        >
          <RotateCcw size={15} className="mr-1.5" /> Reset to defaults
        </Button>
      </div>
    </div>
  );
};

const CompanyComplianceEditor = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({ legal_name: "", mailing_address: "" });

  useEffect(() => {
    let active = true;
    getCompanySetting(token)
      .then((d) => { if (active) setInfo(d.info || { legal_name: "", mailing_address: "" }); })
      .catch(() => toast.error("Failed to load company info"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateCompanySetting(token, info);
      setInfo(res.info || info);
      toast.success("Company info saved \u2014 it now appears in the email footer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save company info");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-5 max-w-2xl flex items-center gap-2 text-white/50">
        <Loader2 size={16} className="animate-spin" /> Loading company info...
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-5 max-w-2xl" data-testid="settings-company-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-[#1E6BFF]/10 p-2 border border-[#1E6BFF]/20">
          <Building2 size={18} className="text-[#66B2FF]" />
        </div>
        <div className="flex-1">
          <span className="text-white font-medium">Company &amp; Compliance Footer</span>
          <p className="text-white/55 text-sm mt-1 max-w-md">
            Shown at the bottom of every outbound marketing email, alongside the unsubscribe link,
            to satisfy CAN-SPAM requirements. Configure once; it applies to all emails.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <label className="text-white/70 text-xs font-medium">Company legal name</label>
          <Input
            value={info.legal_name || ""}
            onChange={(e) => setInfo((i) => ({ ...i, legal_name: e.target.value }))}
            placeholder="Rendeavored Group LLC"
            data-testid="settings-company-legal-name"
            className={`${inputCls} mt-1 h-10`}
          />
        </div>
        <div>
          <label className="text-white/70 text-xs font-medium">Company mailing address</label>
          <Textarea
            value={info.mailing_address || ""}
            onChange={(e) => setInfo((i) => ({ ...i, mailing_address: e.target.value }))}
            placeholder={"123 Example Ave, Suite 100\nCity, ST 00000"}
            rows={2}
            data-testid="settings-company-mailing-address"
            className={`${inputCls} mt-1 resize-y min-h-[64px]`}
          />
          <p className="text-white/35 text-xs mt-1">
            Leave blank until you have a physical mailing address / PO Box to publish. The address
            line only renders once configured.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90"
          data-testid="settings-company-save"
        >
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />}
          Save Company Info
        </Button>
      </div>
    </div>
  );
};


export const SettingsManager = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [envDefault, setEnvDefault] = useState(false);

  useEffect(() => {
    let active = true;
    getPricingSetting(token)
      .then((d) => {
        if (!active) return;
        setShowPricing(!!d.show_pricing);
        setEnvDefault(!!d.env_default);
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const toggle = async (next) => {
    setSaving(true);
    setShowPricing(next); // optimistic
    try {
      const res = await updatePricingSetting(token, next);
      setShowPricing(!!res.show_pricing);
      toast.success(next ? "Pricing is now visible on public pages" : "Pricing is now hidden from public pages");
    } catch (e) {
      setShowPricing(!next); // revert
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-manager">
      <div>
        <h2 className="text-lg font-semibold text-white">Settings</h2>
        <p className="text-white/50 text-sm">Global configuration for the TeamBuilder AI experience.</p>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0A0F1E] p-5 max-w-2xl" data-testid="settings-pricing-card">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-[#2EE59D]/10 p-2 border border-[#2EE59D]/20">
              <DollarSign size={18} className="text-[#2EE59D]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">Pricing Visibility</span>
                <Badge className={`border ${showPricing
                  ? "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30"
                  : "bg-white/10 text-white/60 border-white/15"}`}>
                  {showPricing ? "Visible" : "Hidden"}
                </Badge>
              </div>
              <p className="text-white/55 text-sm mt-1 max-w-md">
                When enabled, estimated investment &amp; savings appear on the public Landing page,
                Assessment preview, Blueprint, and generated PDFs. Internal costs and margins are
                never exposed regardless of this setting.
              </p>
              <p className="text-white/35 text-xs mt-2">
                Environment default: <span className="text-white/60">{envDefault ? "On" : "Off"}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(loading || saving) && <Loader2 size={15} className="animate-spin text-white/40" />}
            <Switch
              checked={showPricing}
              disabled={loading || saving}
              onCheckedChange={toggle}
              data-testid="settings-pricing-toggle"
              aria-label="Toggle public pricing visibility"
            />
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#F5B94A]/25 bg-[#F5B94A]/10 px-3 py-2">
          <AlertTriangle size={15} className="text-[#F5B94A] mt-0.5 shrink-0" />
          <p className="text-[#F5B94A]/90 text-xs">
            This is a global switch. Changes take effect immediately for all visitors and every new
            PDF generated across the public experience.
          </p>
        </div>
      </div>

      <LandingHeroEditor token={token} />

      <CompanyComplianceEditor token={token} />
    </div>
  );
};

export default SettingsManager;
